import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import * as Sentry from '@sentry/angular';
import { environment } from '@workspace-src/environments/environment';
import { SILENT_TOKEN_CACHE_LOOKUP_POLICY } from '@workspace-app/shared/constants/msal-silent-request';
import { LOGIN_FAILED_LOOP_URL, registerAuthRedirect } from './auth-redirect-loop-breaker';

export function loggerCallback(logLevel: LogLevel, message: string) {
  if (!environment.production) {
    console.log(`LogLevel: ${logLevel}, message: ${message}`);
  }
}


// msal-browser 5.19+ no longer persists authority metadata and fetches the B2C
// openid-configuration from the network on EVERY init — an offline reload then dies in
// endpoints_resolution_error before cached tokens can be used. The document is static
// per policy, so we inline it (also saves a network round-trip on every online boot).
// Regenerate if the policy ever changes:
//   curl https://qualicoatworkspace.b2clogin.com/qualicoatworkspace.onmicrosoft.com/b2c_1_signin_v1/v2.0/.well-known/openid-configuration
const B2C_SIGNIN_AUTHORITY_METADATA = JSON.stringify({
  "issuer": "https://qualicoatworkspace.b2clogin.com/ac978ea2-4075-4daf-9887-95bf06344f6a/v2.0/",
  "authorization_endpoint": "https://qualicoatworkspace.b2clogin.com/qualicoatworkspace.onmicrosoft.com/b2c_1_signin_v1/oauth2/v2.0/authorize",
  "token_endpoint": "https://qualicoatworkspace.b2clogin.com/qualicoatworkspace.onmicrosoft.com/b2c_1_signin_v1/oauth2/v2.0/token",
  "end_session_endpoint": "https://qualicoatworkspace.b2clogin.com/qualicoatworkspace.onmicrosoft.com/b2c_1_signin_v1/oauth2/v2.0/logout",
  "jwks_uri": "https://qualicoatworkspace.b2clogin.com/qualicoatworkspace.onmicrosoft.com/b2c_1_signin_v1/discovery/v2.0/keys",
  "response_modes_supported": [
    "query",
    "fragment",
    "form_post"
  ],
  "response_types_supported": [
    "code",
    "code id_token",
    "code token",
    "code id_token token",
    "id_token",
    "id_token token",
    "token",
    "token id_token"
  ],
  "scopes_supported": [
    "openid"
  ],
  "subject_types_supported": [
    "pairwise"
  ],
  "id_token_signing_alg_values_supported": [
    "RS256"
  ],
  "token_endpoint_auth_methods_supported": [
    "client_secret_post",
    "client_secret_basic"
  ],
  "claims_supported": [
    "country",
    "name",
    "emails",
    "idp",
    "oid",
    "sub",
    "tfp",
    "isForgotPassword",
    "iss",
    "iat",
    "exp",
    "aud",
    "acr",
    "nonce",
    "auth_time"
  ]
});

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signIn.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
      knownAuthorities: [environment.b2cPolicies.authorityDomain],
      authorityMetadata: B2C_SIGNIN_AUTHORITY_METADATA,
      // Called before every redirect to B2C (sign-in and logout); returning false stops the navigation.
      onRedirectNavigate: (url) => {
        if (registerAuthRedirect(url)) {
          return true;
        }
        // No-op when Sentry is not initialized (local dev).
        Sentry.captureMessage('Sign-in redirect loop stopped by the auth loop breaker', 'warning');
        window.location.assign(LOGIN_FAILED_LOOP_URL);
        return false;
      },
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(
    `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.apiUri}`,
    environment.apiConfig.scopes
  );
  protectedResourceMap.set(
    `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.odataUri}`,
    environment.apiConfig.scopes
  );
  protectedResourceMap.set(
    `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.notificationsUri}`,
    environment.apiConfig.scopes
  );
  protectedResourceMap.set(
    `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.eventsUri}`,
    environment.apiConfig.scopes
  );

  // Print the protected resource map for debugging purposes
  if (!environment.production) {
    console.log('Protected Resource Map:', protectedResourceMap);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    // Merged into every acquireTokenSilent the interceptor makes; see the constant for why.
    authRequest: { cacheLookupPolicy: SILENT_TOKEN_CACHE_LOOKUP_POLICY },
    strictMatching: false,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/auth/login-failed',
  };
}

export function initializeMsalFactory(msalService: MsalService): () => Promise<void> {
  return () => {
    return msalService.instance.initialize();
  };
}
